import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ROLE_LABELS, TENANT_ROLES, type OidcConnectionDto, type TenantRole } from '@imsenta/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  TagInput,
  useToast,
} from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { fmtDateTime, fmtRelative } from '@/lib/format';
import { useSession } from '@/lib/session';

interface ConnectionsResponse {
  items: OidcConnectionDto[];
  redirectUri: string;
  enabled: boolean;
}

export function SsoSettings() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [editing, setEditing] = useState<OidcConnectionDto | 'new' | null>(null);
  const [deleting, setDeleting] = useState<OidcConnectionDto | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sso-connections'],
    queryFn: () => api.get<ConnectionsResponse>('/sso/connections'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/sso/connections/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sso-connections'] });
      setDeleting(null);
      push('Connection removed', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not remove the connection', 'danger'),
  });

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <Card>
        <ErrorState error={error} onRetry={() => void refetch()} title="Could not load single sign-on settings" />
      </Card>
    );
  }

  const readOnly = !can('tenant:manage');

  return (
    <>
      <div className="space-y-5">
        {!data?.enabled ? (
          <Alert tone="warning" title="Single sign-on is switched off on this server">
            Connections can be configured, but nobody will be able to use them until an operator sets{' '}
            <code className="font-mono text-[12px]">OIDC_ENABLED=true</code>.
          </Alert>
        ) : null}

        <Card>
          <CardHeader
            title="Single sign-on"
            description="Let people sign in with your organisation's identity provider using OpenID Connect."
            actions={
              !readOnly ? (
                <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
                  Add a connection
                </Button>
              ) : null
            }
          />

          {!data?.items.length ? (
            <EmptyState
              icon={<KeyRound className="size-10" />}
              title="No identity provider connected"
              description="Connect Entra ID, Okta, Google Workspace, Keycloak or any OpenID Connect provider."
              action={
                !readOnly ? (
                  <Button variant="primary" onClick={() => setEditing('new')}>
                    Add a connection
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.items.map((connection) => (
                <li key={connection.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5">
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600">
                    <KeyRound className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1 basis-52">
                    <p className="flex flex-wrap items-center gap-2 text-[13.5px] font-medium text-ink-800">
                      {connection.name}
                      {connection.enabled ? <Badge tone="success">enabled</Badge> : <Badge tone="neutral">disabled</Badge>}
                      {connection.config.autoProvision ? <Badge tone="info">auto-provision</Badge> : null}
                    </p>
                    <p className="truncate text-[12px] text-ink-500">
                      {connection.issuer}
                      {connection.emailDomains.length ? ` · ${connection.emailDomains.join(', ')}` : ''}
                    </p>
                  </div>
                  <span className="text-[11.5px] text-ink-400">
                    {connection.lastUsedAt ? `Last used ${fmtRelative(connection.lastUsedAt)}` : 'Never used'}
                  </span>
                  {!readOnly ? (
                    <>
                      <Button size="sm" variant="primary" onClick={() => setEditing(connection)}>
                        Configure
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Trash2 className="size-3.5" />}
                        onClick={() => setDeleting(connection)}
                        aria-label={`Remove ${connection.name}`}
                      />
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>

        {data?.redirectUri ? (
          <Card>
            <CardHeader title="What your provider needs" description="Register these values in the application you create there." />
            <dl className="space-y-3 px-5 py-4 text-[13px]">
              <CopyRow label="Redirect URI" value={data.redirectUri} />
              <CopyRow label="Response type" value="code" />
              <CopyRow label="PKCE" value="S256 (required)" copyable={false} />
              <CopyRow label="Scopes" value="openid profile email" />
            </dl>
          </Card>
        ) : null}
      </div>

      {editing ? (
        <ConnectionModal
          connection={editing === 'new' ? null : editing}
          redirectUri={data?.redirectUri ?? ''}
          onClose={() => setEditing(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['sso-connections'] });
            setEditing(null);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        title={`Remove “${deleting?.name}”?`}
        message="People who sign in through it will lose that route immediately. Their accounts and any password they have are kept."
        confirmLabel="Remove connection"
        loading={remove.isPending}
      />
    </>
  );
}

function CopyRow({ label, value, copyable = true }: { label: string; value: string; copyable?: boolean }) {
  const { push } = useToast();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <dt className="w-32 shrink-0 text-ink-500">{label}</dt>
      <dd className="min-w-0 flex-1">
        <code className="block truncate rounded bg-ink-100 px-2 py-1 font-mono text-[12px] text-ink-700">{value}</code>
      </dd>
      {copyable ? (
        <Button
          size="sm"
          icon={<Copy className="size-3.5" />}
          onClick={() => {
            void navigator.clipboard.writeText(value);
            push(`${label} copied`, 'success');
          }}
          aria-label={`Copy ${label}`}
        />
      ) : null}
    </div>
  );
}

interface TestResult {
  ok: boolean;
  warnings: string[];
  endpoints: { authorization: string; token: string; jwks: string };
  signingAlgorithms: string[];
}

function ConnectionModal({
  connection,
  redirectUri,
  onClose,
  onSaved,
}: {
  connection: OidcConnectionDto | null;
  redirectUri: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push } = useToast();
  const [name, setName] = useState(connection?.name ?? '');
  const [issuer, setIssuer] = useState(connection?.issuer ?? '');
  const [clientId, setClientId] = useState(connection?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [enabled, setEnabled] = useState(connection?.enabled ?? true);
  const [emailDomains, setEmailDomains] = useState<string[]>(connection?.emailDomains ?? []);
  const [config, setConfig] = useState(
    connection?.config ?? {
      scopes: ['openid', 'profile', 'email'],
      emailClaim: 'email',
      nameClaim: 'name',
      groupsClaim: 'groups',
      roleMappings: {} as Record<string, TenantRole>,
      defaultRole: 'reader' as TenantRole,
      autoProvision: true,
      linkByVerifiedEmail: false,
      syncRolesOnSignIn: true,
      allowedEmailDomains: [],
    },
  );
  const [mappings, setMappings] = useState<{ group: string; role: TenantRole }[]>(
    Object.entries(connection?.config.roleMappings ?? {}).map(([group, role]) => ({ group, role })),
  );
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const payload = () => ({
    name: name.trim(),
    issuer: issuer.trim().replace(/\/+$/, ''),
    clientId: clientId.trim(),
    // Omitted keeps the stored secret; an empty string is only sent deliberately.
    ...(clientSecret === '' ? {} : { clientSecret }),
    enabled,
    emailDomains,
    config: {
      ...config,
      roleMappings: Object.fromEntries(mappings.filter((m) => m.group.trim()).map((m) => [m.group.trim(), m.role])),
    },
  });

  const save = useMutation({
    mutationFn: () =>
      connection ? api.put(`/sso/connections/${connection.id}`, payload()) : api.post('/sso/connections', payload()),
    onSuccess: () => {
      push(connection ? 'Connection updated' : 'Connection added', 'success');
      onSaved();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save the connection'),
  });

  const test = useMutation({
    mutationFn: () => api.post<TestResult>(`/sso/connections/${connection!.id}/test`),
    onSuccess: (result) => {
      setTestResult(result);
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not reach the identity provider'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      width="lg"
      title={connection ? `Configure “${connection.name}”` : 'Connect an identity provider'}
      description="Imsenta uses the OpenID Connect authorisation code flow with PKCE."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {connection ? (
            <Button icon={<ShieldCheck className="size-4" />} loading={test.isPending} onClick={() => test.mutate()}>
              Test connection
            </Button>
          ) : null}
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!name.trim() || !issuer.trim() || !clientId.trim()}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
          >
            {connection ? 'Save connection' : 'Add connection'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}

        {testResult ? (
          <Alert tone={testResult.warnings.length ? 'warning' : 'success'} title="The provider responded">
            <p className="font-mono text-[12px] break-all">{testResult.endpoints.token}</p>
            {testResult.signingAlgorithms.length ? (
              <p className="mt-1">Signing algorithms: {testResult.signingAlgorithms.join(', ')}</p>
            ) : null}
            {testResult.warnings.length ? (
              <ul className="mt-1 list-inside list-disc">
                {testResult.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            ) : null}
          </Alert>
        ) : null}

        <Field label="Display name" required hint="Shown on the sign-in button.">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Contoso Entra ID" />
        </Field>

        <Field
          label="Issuer URL"
          required
          hint="The base URL. We read its /.well-known/openid-configuration to find the rest."
        >
          <Input
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
            placeholder="https://login.microsoftonline.com/<tenant-id>/v2.0"
            className="font-mono text-[12.5px]"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Client ID" required>
            <Input value={clientId} onChange={(e) => setClientId(e.target.value)} className="font-mono text-[12.5px]" />
          </Field>
          <Field
            label="Client secret"
            hint={connection?.clientSecretSet ? 'A secret is stored. Enter a new one to replace it.' : 'Leave blank for a public client.'}
          >
            <Input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={connection?.clientSecretSet ? '•••••••••••• (stored)' : ''}
              autoComplete="off"
              className="font-mono text-[12.5px]"
            />
          </Field>
        </div>

        {redirectUri ? (
          <Field label="Redirect URI" hint="Register exactly this value with your provider.">
            <Input value={redirectUri} readOnly onFocus={(e) => e.currentTarget.select()} className="font-mono text-[12px]" />
          </Field>
        ) : null}

        <Field
          label="Email domains"
          hint="Someone typing an address at one of these domains is sent to this provider. A domain can only be claimed by one organisation."
        >
          <TagInput value={emailDomains} onChange={setEmailDomains} placeholder="contoso.com" />
        </Field>

        <div className="rounded-lg border border-ink-200 p-4">
          <p className="mb-3 text-[11px] font-semibold tracking-wider text-ink-500 uppercase">Claims</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Email claim">
              <Input value={config.emailClaim} onChange={(e) => setConfig({ ...config, emailClaim: e.target.value })} className="font-mono text-[12.5px]" />
            </Field>
            <Field label="Name claim">
              <Input value={config.nameClaim} onChange={(e) => setConfig({ ...config, nameClaim: e.target.value })} className="font-mono text-[12.5px]" />
            </Field>
            <Field label="Groups claim" hint="Blank to ignore groups.">
              <Input
                value={config.groupsClaim ?? ''}
                onChange={(e) => setConfig({ ...config, groupsClaim: e.target.value || null })}
                className="font-mono text-[12.5px]"
              />
            </Field>
          </div>
        </div>

        <div className="rounded-lg border border-ink-200 p-4">
          <p className="mb-1 text-[11px] font-semibold tracking-wider text-ink-500 uppercase">Group to role mapping</p>
          <p className="mb-3 text-[12.5px] text-ink-500">
            When someone signs in, the most privileged matching role is applied. Anyone with no matching group gets the default.
          </p>

          <div className="space-y-2">
            {mappings.map((mapping, index) => (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <Input
                  value={mapping.group}
                  onChange={(e) => setMappings(mappings.map((m, i) => (i === index ? { ...m, group: e.target.value } : m)))}
                  placeholder="Group name or object id"
                  className="min-w-0 flex-1 font-mono text-[12.5px]"
                  aria-label={`Group ${index + 1}`}
                />
                <Select
                  value={mapping.role}
                  onChange={(e) => setMappings(mappings.map((m, i) => (i === index ? { ...m, role: e.target.value as TenantRole } : m)))}
                  className="w-auto"
                  aria-label={`Role for group ${index + 1}`}
                >
                  {TENANT_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="size-3.5" />}
                  onClick={() => setMappings(mappings.filter((_, i) => i !== index))}
                  aria-label={`Remove mapping ${index + 1}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-4">
            <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setMappings([...mappings, { group: '', role: 'reader' }])}>
              Add mapping
            </Button>
            <Field label="Default role" className="w-44">
              <Select value={config.defaultRole} onChange={(e) => setConfig({ ...config, defaultRole: e.target.value as TenantRole })}>
                {TENANT_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>

        <div className="space-y-3 rounded-lg border border-ink-200 p-4">
          <p className="text-[11px] font-semibold tracking-wider text-ink-500 uppercase">Accounts</p>
          <Checkbox
            label="Create an account on first sign-in"
            hint="Off means only people you have already invited can use SSO."
            checked={config.autoProvision}
            onChange={(e) => setConfig({ ...config, autoProvision: e.target.checked })}
          />
          <Checkbox
            label="Re-apply the role mapping on every sign-in"
            hint="Off lets you promote someone here without your provider undoing it."
            checked={config.syncRolesOnSignIn}
            onChange={(e) => setConfig({ ...config, syncRolesOnSignIn: e.target.checked })}
          />
          <Checkbox
            label="Link to an existing account with the same email address"
            hint="Only ever applied when your provider confirms the address is verified. Leave off unless people already have password accounts here."
            checked={config.linkByVerifiedEmail}
            onChange={(e) => setConfig({ ...config, linkByVerifiedEmail: e.target.checked })}
          />
          <Checkbox
            label="Enabled"
            hint="Turn off to suspend SSO without losing the configuration."
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        </div>

        {connection ? (
          <p className="text-[12px] text-ink-400">
            Added {fmtDateTime(connection.createdAt)}
            {connection.discoveredAt ? ` · configuration last read ${fmtRelative(connection.discoveredAt)}` : ''}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
